
import { supabase } from "@/integrations/supabase/client";
import { Reagent } from "@/types/reagent.types";
import { toast } from "sonner";
import { createLogger } from "@/services/utils/loggingUtils";
import { dateToString } from "@/utils/dateUtils";

const logger = createLogger("UpdateReagentService");

// Update a reagent
export const updateReagent = async (id: string, updateData: Partial<Reagent>): Promise<boolean> => {
  try {
    // Remove any fields that shouldn't be updated
    const { id: _, profiles, categories, created_at, is_deleted, available, user_id, ...updatableData } = updateData as any;
    
    // Convert any camelCase to snake_case for the database
    const dbData: any = {};
    
    // Map camelCase properties to snake_case for the database
    if (updatableData.catalogNumber) dbData.catalogue_number = updatableData.catalogNumber;
    if (updatableData.storageConditions) dbData.storage_conditions = updatableData.storageConditions;
    if (updatableData.numberOfPackages) dbData.number_of_packages = updatableData.numberOfPackages;
    if (updatableData.expiryDate) dbData.expiry_date = dateToString(updatableData.expiryDate);
    
    // Copy over properties that don't need mapping
    const directProps = ['name', 'description', 'category_id', 'image_url', 'city', 'country'];
    for (const prop of directProps) {
      if (updatableData[prop] !== undefined) dbData[prop] = updatableData[prop];
    }
    
    // Handle amount specially, as it needs conversion between string and number
    if (updatableData.amount !== undefined) {
      // Parse amount to a number if it's stored as a string
      const amountValue = updatableData.amount;
      dbData.amount = parseFloat(amountValue);
      
      if (isNaN(dbData.amount)) {
        // If parsing fails, try to extract numeric part
        const numericPart = amountValue.match(/[\d.]+/);
        dbData.amount = numericPart ? parseFloat(numericPart[0]) : 0;
      }
    }
    
    // Add updated timestamp
    dbData.updated_at = new Date().toISOString();
    
    logger.info("Updating reagent with data:", dbData);
    
    const { error } = await supabase
      .from("reagents")
      .update(dbData)
      .eq("id", id);

    if (error) {
      logger.error("Error updating reagent:", error);
      toast.error("Failed to update reagent: " + error.message);
      return false;
    }

    logger.info("Reagent updated successfully");
    toast.success("Reagent updated successfully!");
    return true;
  } catch (error) {
    logger.error("Exception in updateReagent:", error);
    return false;
  }
};
